import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, Button, Alert, Spin } from 'antd';
import { GoogleOutlined, LockOutlined } from '@ant-design/icons';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebase/config';
import { theme } from '../../../styles/theme';

const AdminRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUser(user);
        // Check if user is admin
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsAdmin(userData.role === 'admin' || userData.isSuperUser === true);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);


  const handleGoogleSignIn = async () => {
    setLoginError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if this is a new user and create admin profile
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        // Create admin user document for Google sign-in
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          name: result.user.displayName,
          role: 'admin',
          isSuperUser: true,
          createdAt: new Date(),
          permissions: ['analytics', 'admin_dashboard', 'user_management'],
          provider: 'google'
        });
      }
      
      // The auth state change will handle the rest
    } catch (error) {
      console.error('Google sign-in error:', error);
      setLoginError('Google sign-in failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: theme.colors.lightGray
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: theme.colors.lightGray,
        padding: '20px'
      }}>
        <Card 
          title={
            <div style={{ textAlign: 'center', color: theme.colors.primary }}>
              <GoogleOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
              Admin Access Required
            </div>
          }
          style={{ 
            width: '100%', 
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <p style={{ 
            textAlign: 'center', 
            color: theme.colors.gray, 
            marginBottom: '24px',
            fontFamily: theme.fonts.primary
          }}>
            Please sign in with your Google account to access the analytics dashboard.
          </p>
          
          {loginError && (
            <Alert
              message={loginError}
              type="error"
              style={{ marginBottom: '16px' }}
            />
          )}

          <Button
            onClick={handleGoogleSignIn}
            block
            icon={<GoogleOutlined />}
            size="large"
            style={{
              fontFamily: theme.fonts.primary,
              height: '50px',
              borderColor: '#db4437',
              color: '#db4437',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            Continue with Google
          </Button>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: theme.colors.lightGray,
        padding: '20px'
      }}>
        <Card 
          title={
            <div style={{ textAlign: 'center', color: theme.colors.error }}>
              <LockOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
              Access Denied
            </div>
          }
          style={{ 
            width: '100%', 
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <p style={{ 
              color: theme.colors.gray, 
              marginBottom: '24px',
              fontFamily: theme.fonts.primary
            }}>
              You don't have admin privileges to access this page.
            </p>
            <p style={{ 
              color: theme.colors.gray, 
              marginBottom: '24px',
              fontFamily: theme.fonts.primary,
              fontSize: '14px'
            }}>
              Signed in as: <strong>{user.email}</strong>
            </p>
            <Button
              onClick={handleLogout}
              style={{
                fontFamily: theme.fonts.primary
              }}
            >
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // User is authenticated and is admin
  return (
    <div>
      {children}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <Button
          onClick={handleLogout}
          size="small"
          style={{
            backgroundColor: theme.colors.error,
            borderColor: theme.colors.error,
            color: 'white'
          }}
        >
          Sign Out ({user.email})
        </Button>
      </div>
    </div>
  );
};

export default AdminRoute;
